<script setup lang="ts">
import type { PaginationQueryParams } from '@/@vxapp/@Enums/PaginationField'
import { PaginationField } from '@/@vxapp/@Enums/PaginationField'
import { A } from '@/@vxapp/@Enums/PHP/Auth/DataProviders/AbilityAction'
import { ResponseCode } from '@/@vxapp/@Enums/PHP/HTTP/ResponseCode'
import { createLogger } from '@/@vxapp/@core/Logger/VxLog'
import { cloneReactiveData } from '@/@core/utils/cloneReactiveData'
import { useContractStore } from '@/@vxapp/Contracts/useContractStore'
import { setPageTitle } from '@/@core/utils/pageMetaUtils'
import ContractDialog from '@/components/Dialogs/Contracts/ContractDialog.vue'
import ContractsDataTable from '@/components/Tables/Contracts/ContractsDataTable.vue'
import { DialogDisplayType } from '@/enums/dialog-display-type'

type ContractData = Record<string, unknown>
type DialogDisplayTypeValue = (typeof DialogDisplayType)[keyof typeof DialogDisplayType]
type ManagedQueryKey = 'page' | 'limit' | 'sort' | 'search'

definePage({
  meta: {
    action: A.READ,
    subject: 'Contracts:Preview:All',
  },
})
setPageTitle('Contracts')

const log = createLogger('ContractsListPage')
const route = useRoute()
const router = useRouter()
const contractStore = useContractStore() as any
const contractsDataTable = ref<InstanceType<typeof ContractsDataTable> | null>(null)
const contractData = ref<ContractData | null>(null)
const dialogDisplayType = shallowRef<DialogDisplayTypeValue>(DialogDisplayType.CREATE)
const isContractDialogVisible = shallowRef(false)
const isRouteQueryReady = shallowRef(false)
const searchQuery = ref('')
const resultCount = ref(0)
const defaultQueryParams: PaginationQueryParams = { ...PaginationField.QueryParams, sort: '+id' }
const managedQueryKeys: ManagedQueryKey[] = ['page', 'limit', 'sort', 'search']

const sortItems = [
  { title: 'Oldest first', value: '+id' },
  { title: 'Newest first', value: '-id' },
]

const limitItems = [10, 25, 50, 100]

const normalizeQueryValue = (value: unknown): string | undefined => {
  const normalizedValue = Array.isArray(value) ? value[0] : value

  if (typeof normalizedValue === 'number') return String(normalizedValue)

  return typeof normalizedValue === 'string' ? normalizedValue : undefined
}

const normalizePositiveInteger = (value: unknown, fallback: number) => {
  const normalizedValue = normalizeQueryValue(value)
  const parsedValue = normalizedValue ? Number.parseInt(normalizedValue, 10) : Number.NaN

  return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : fallback
}

const normalizeQueryParams = (source: Record<string, unknown>): PaginationQueryParams => {
  const parsedLimit = Number.parseInt(normalizeQueryValue(source.limit) ?? '', 10)
  const search = normalizeQueryValue(source.search)?.trim()
  const sort = normalizeQueryValue(source.sort)

  return {
    ...defaultQueryParams,
    page: normalizePositiveInteger(source.page, defaultQueryParams.page),
    limit:
      parsedLimit === -1
        ? parsedLimit
        : normalizePositiveInteger(source.limit, defaultQueryParams.limit),
    sort: sort === '+id' || sort === '-id' ? sort : defaultQueryParams.sort,
    search: search || undefined,
  }
}

const queryParamsMatch = (left: PaginationQueryParams, right: PaginationQueryParams) =>
  left.page === right.page &&
  left.limit === right.limit &&
  left.sort === right.sort &&
  left.search === right.search

const applyRouteQuery = () => {
  const currentQuery = normalizeQueryParams(contractStore.queryParams ?? defaultQueryParams)
  const routeQuery = normalizeQueryParams(route.query as Record<string, unknown>)

  if (queryParamsMatch(currentQuery, routeQuery)) return false

  contractStore.queryParams = { ...contractStore.queryParams, ...routeQuery }
  searchQuery.value = routeQuery.search ?? ''

  return true
}

const buildRouteQuery = (
  source: Record<string, unknown> = contractStore.queryParams ?? defaultQueryParams
) => {
  const nextQuery = { ...route.query } as Record<string, unknown>

  for (const key of managedQueryKeys) delete nextQuery[key]

  const queryParams = normalizeQueryParams(source)

  return {
    ...nextQuery,
    ...(queryParams.page !== defaultQueryParams.page ? { page: String(queryParams.page) } : {}),
    ...(queryParams.limit !== defaultQueryParams.limit ? { limit: String(queryParams.limit) } : {}),
    ...(queryParams.sort !== defaultQueryParams.sort ? { sort: queryParams.sort } : {}),
    ...(queryParams.search ? { search: queryParams.search } : {}),
  }
}

const syncRouteQuery = async (queryParams: PaginationQueryParams) => {
  const nextQuery = buildRouteQuery({ ...queryParams })

  if (JSON.stringify(route.query) !== JSON.stringify(nextQuery))
    await router.replace({ query: nextQuery })
}

const fetchContracts = async () => {
  await contractsDataTable.value?.fetchContracts()
}

const updateQueryParams = async (patch: Partial<PaginationQueryParams>) => {
  const nextQueryParams = normalizeQueryParams({
    ...contractStore.queryParams,
    ...patch,
  })

  contractStore.queryParams = nextQueryParams
  await syncRouteQuery(nextQueryParams)
  await nextTick()
  await fetchContracts()
}

const handleSearch = async () => {
  await updateQueryParams({ search: searchQuery.value.trim() || undefined, page: 1 })
}

const handleSearchInput = (value: unknown) => {
  searchQuery.value = String(value ?? '')
}

const handleClear = async () => {
  searchQuery.value = ''
  await updateQueryParams({ search: undefined, page: 1 })
}

const handleSortChange = async (value: unknown) => {
  await updateQueryParams({ sort: String(value), page: 1 })
}

const handleLimitChange = async (value: unknown) => {
  await updateQueryParams({ limit: Number(value), page: 1 })
}

const handlePageChange = async (page: number) => {
  await updateQueryParams({ page })
}

const handleLoaded = (entriesFound: number) => {
  resultCount.value = entriesFound
}

const handleCreateContract = async () => {
  try {
    const { responseCode } = await contractStore.fetchTemplate()

    if (responseCode !== ResponseCode.OK) {
      log.error('Failed to load contract template', { responseCode })

      return
    }

    contractData.value = cloneReactiveData(contractStore.template ?? {})
    dialogDisplayType.value = DialogDisplayType.CREATE
    isContractDialogVisible.value = true
  } catch (error) {
    log.error('Failed to load contract template', error)
  }
}

const openContractDialog = async (contractGUID: string, displayType: DialogDisplayTypeValue) => {
  try {
    const { responseCode } = await contractStore.fetch(contractGUID)

    if (responseCode !== ResponseCode.OK) {
      log.error('Failed to load contract', { contractGUID, responseCode })

      return
    }

    contractData.value = cloneReactiveData(contractStore.contract ?? {})
    dialogDisplayType.value = displayType
    isContractDialogVisible.value = true
  } catch (error) {
    log.error('Failed to load contract', error)
  }
}

const handleEditContract = async (contractGUID: string) => {
  await openContractDialog(contractGUID, DialogDisplayType.EDIT)
}

const handleViewContract = async (contractGUID: string) => {
  await openContractDialog(contractGUID, DialogDisplayType.VIEW)
}

const handleSaveContract = async (newContractData: ContractData) => {
  if (dialogDisplayType.value === DialogDisplayType.VIEW) return

  try {
    const response =
      dialogDisplayType.value === DialogDisplayType.CREATE
        ? await contractStore.create(newContractData, false)
        : await contractStore.update(newContractData, false)

    const expectedCode =
      dialogDisplayType.value === DialogDisplayType.CREATE ? ResponseCode.CREATED : ResponseCode.OK

    if (response.responseCode !== expectedCode) {
      log.error('Failed to save contract', { responseCode: response.responseCode })

      return
    }

    await fetchContracts()
  } catch (error) {
    log.error('Failed to save contract', error)
  }
}

contractStore.queryParams = { ...defaultQueryParams }
applyRouteQuery()
isRouteQueryReady.value = true

onMounted(fetchContracts)

watch(
  () => route.query,
  async () => {
    if (!isRouteQueryReady.value || !applyRouteQuery()) return

    await nextTick()
    await fetchContracts()
  },
  { deep: true }
)
</script>

<template>
  <main class="contracts-workspace">
    <section class="contracts-workspace__hero">
      <div>
        <span>Legal workspace</span>
        <h1>Your agreement library.</h1>
        <p>
          Find contract documents, open their full record, and keep reusable agreements current.
        </p>
      </div>
      <div class="contracts-workspace__hero-actions">
        <div>
          <span>Documents</span>
          <strong>{{ resultCount.toLocaleString() }}</strong>
        </div>
        <VBtn
          color="primary"
          prepend-icon="tabler-plus"
          size="large"
          @click="handleCreateContract"
        >
          New contract
        </VBtn>
      </div>
    </section>

    <VCard class="contracts-workspace__toolbar">
      <VCardText>
        <VForm
          class="contracts-workspace__search"
          role="search"
          @submit.prevent="handleSearch"
        >
          <!-- vortex-validation-ignore-element: server search is non-validation filter UI -->
          <AppTextField
            :model-value="searchQuery"
            aria-label="Search contracts"
            clearable
            hide-details
            placeholder="Search contract names"
            prepend-inner-icon="tabler-search"
            vortex-validation-ignore-element
            @click:clear="handleClear"
            @update:model-value="handleSearchInput"
          />
          <VBtn
            color="primary"
            type="button"
            @click="handleSearch"
          >
            Search
          </VBtn>
        </VForm>
        <!-- vortex-validation-ignore-element: result sorting is non-validation UI -->
        <AppSelect
          :model-value="contractStore.queryParams?.sort ?? defaultQueryParams.sort"
          :items="sortItems"
          aria-label="Sort contracts"
          hide-details
          label="Sort"
          vortex-validation-ignore-element
          @update:model-value="handleSortChange"
        />
        <!-- vortex-validation-ignore-element: page size is non-validation UI -->
        <AppSelect
          :model-value="contractStore.queryParams?.limit ?? defaultQueryParams.limit"
          :items="limitItems"
          aria-label="Contracts per page"
          hide-details
          label="Per page"
          vortex-validation-ignore-element
          @update:model-value="handleLimitChange"
        />
      </VCardText>
    </VCard>

    <ContractsDataTable
      ref="contractsDataTable"
      @edit="handleEditContract"
      @loaded="handleLoaded"
      @page-change="handlePageChange"
      @view="handleViewContract"
    />
  </main>

  <ContractDialog
    v-if="contractData"
    v-model:is-dialog-visible="isContractDialogVisible"
    :display-type="dialogDisplayType"
    :contract-data="contractData"
    @create="handleSaveContract"
    @update="handleSaveContract"
  />
</template>

<style scoped lang="scss">
.contracts-workspace {
  display: grid;
  max-inline-size: 75rem;
  margin-inline: auto;
  color: rgba(var(--v-theme-on-background), var(--v-high-emphasis-opacity));
  font-family:
    Inter,
    -apple-system,
    system-ui,
    'Segoe UI',
    sans-serif;
  gap: 1rem;
}

.contracts-workspace__hero {
  display: grid;
  align-items: end;
  border-radius: 1rem;
  background: rgb(var(--v-theme-surface-bright));
  gap: 2rem;
  grid-template-columns: minmax(0, 1.4fr) minmax(15rem, 0.6fr);
  padding: clamp(2rem, 6vw, 5rem);
}

.contracts-workspace__hero > div:first-child > span,
.contracts-workspace__hero-actions span {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  font-size: 0.75rem;
  font-weight: 600;
}

.contracts-workspace__hero h1 {
  margin-block: 0.75rem 1rem;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  font-size: clamp(2.5rem, 6vw, 4rem);
  font-weight: 700;
  letter-spacing: -0.045em;
  line-height: 1;
}

.contracts-workspace__hero p {
  max-inline-size: 42rem;
  margin: 0;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  font-size: 1.05rem;
  line-height: 1.5;
}

.contracts-workspace__hero-actions {
  display: grid;
  gap: 1rem;
  justify-items: end;
}

.contracts-workspace__hero-actions > div {
  display: grid;
  justify-items: end;
}

.contracts-workspace__hero-actions strong {
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  font-size: 2.75rem;
  letter-spacing: -0.05em;
}

.contracts-workspace :deep(.v-btn) {
  min-block-size: 2.75rem;
  border-radius: 0.25rem;
}

.contracts-workspace__toolbar {
  border: 1px solid rgba(var(--v-border-color), 0.14);
  border-radius: 0.75rem;
  box-shadow: none;
}

.contracts-workspace__toolbar :deep(.v-card-text) {
  display: grid;
  align-items: end;
  gap: 0.75rem;
  grid-template-columns: minmax(16rem, 1fr) minmax(10rem, 0.35fr) minmax(8rem, 0.25fr);
}

.contracts-workspace__search {
  display: flex;
  align-items: end;
  gap: 0.75rem;
}

.contracts-workspace__search :deep(.v-input) {
  min-inline-size: 0;
}

@media (max-width: 899px) {
  .contracts-workspace__hero {
    grid-template-columns: minmax(0, 1fr);
  }

  .contracts-workspace__hero-actions,
  .contracts-workspace__hero-actions > div {
    justify-items: start;
  }

  .contracts-workspace__toolbar :deep(.v-card-text) {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 599px) {
  .contracts-workspace__hero {
    padding: 1.5rem;
  }

  .contracts-workspace__hero-actions :deep(.v-btn) {
    inline-size: 100%;
  }

  .contracts-workspace__search {
    align-items: stretch;
    flex-direction: column;
  }

  .contracts-workspace__search :deep(.v-btn) {
    inline-size: 100%;
  }
}
</style>
